import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { DisclosureHost } from "../../hooks/use-disclosure";
import { FullscriptApi } from "..";
import { getConfigObject } from "../../utils/utils";

let FullscriptLib: unknown = null;

type OAuthTokenResponse = {
  hasToken?: boolean;
  redirectUrl?: string;
};

interface FullScriptIframeProps {
  disclosureHost: DisclosureHost;
  xTENANTID?: string;
  entrypoint?: string;
}

const loadFullscript = async () => {
  if (FullscriptLib) return FullscriptLib;

  try {
    const module = await import("@fullscript/fullscript-js");
    FullscriptLib = module.Fullscript || module.default || module;
    return FullscriptLib;
  } catch (error) {
    return null;
  }
};

const getObject = (payload: unknown): Record<string, unknown> => {
  if (!payload || typeof payload !== "object") {
    return {};
  }
  return payload as Record<string, unknown>;
};

const parseOAuthTokenResponse = (payload: unknown): OAuthTokenResponse => {
  const source = getObject(payload);
  return {
    hasToken:
      typeof source.hasToken === "boolean" ? source.hasToken : undefined,
    redirectUrl:
      typeof source.redirectUrl === "string" ? source.redirectUrl : undefined,
  };
};

const waitForMountPoint = (
  elementId: string,
  timeoutMs = 8000
): Promise<HTMLElement> =>
  new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      const mountElement = document.getElementById(elementId);
      if (mountElement) {
        window.clearInterval(interval);
        resolve(mountElement);
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        window.clearInterval(interval);
        reject(
          new Error(
            `Could not find the mount point for the iframe: ${elementId}`
          )
        );
      }
    }, 50);
  });

const FullScriptIframe = ({
  disclosureHost,
  xTENANTID,
  entrypoint = "labs",
}: FullScriptIframeProps) => {
  const { opened: isOpen, close } = disclosureHost;
  const [isLoading, setIsLoading] = useState(false);
  const [showOAuthMessage, setShowOAuthMessage] = useState(false);
  const [secretToken, setSecretToken] = useState<string | null>(null);

  const mountContainerIdRef = useRef(
    `fullscript-embed-container-${Math.random().toString(36).slice(2, 10)}`
  );
  const featureRef = useRef<{ unmount?: () => void } | null>(null);
  const popupWindowRef = useRef<Window | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const processingOAuthRef = useRef(false);

  const clearPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const closePopup = () => {
    if (popupWindowRef.current && !popupWindowRef.current.closed) {
      popupWindowRef.current.close();
    }
    popupWindowRef.current = null;
  };

  const getOAuthToken = async (): Promise<OAuthTokenResponse> => {
    const response = await new FullscriptApi(
      getConfigObject()
    ).getAccessTokenOrRedirect(xTENANTID);
    return parseOAuthTokenResponse(response?.data?.data ?? response?.data);
  };

  const getSessionGrant = async (): Promise<string | null> => {
    const response = await new FullscriptApi(
      getConfigObject()
    ).createSessionGrant(xTENANTID);
    const payload = (response?.data?.data ?? response?.data) as any;
    return payload?.session_grant?.secret_token ?? null;
  };

  const requestSessionGrant = async () => {
    try {
      setIsLoading(true);
      const grantToken = await getSessionGrant();
      if (!grantToken) {
        throw new Error("Session grant token not received");
      }

      setShowOAuthMessage(false);
      setSecretToken(grantToken);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const checkTokenAfterPopup = async () => {
    if (processingOAuthRef.current) return;
    processingOAuthRef.current = true;

    try {
      const oauthData = await getOAuthToken();
      if (oauthData.hasToken) {
        await requestSessionGrant();
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    } finally {
      processingOAuthRef.current = false;
    }
  };

  const startPopupPolling = () => {
    clearPolling();
    pollIntervalRef.current = setInterval(() => {
      if (!popupWindowRef.current) return;

      if (popupWindowRef.current.closed) {
        clearPolling();
        setTimeout(() => {
          void checkTokenAfterPopup();
        }, 1000);
      }
    }, 1000);
  };

  useEffect(() => {
    if (!isOpen) {
      clearPolling();
      closePopup();
      setIsLoading(false);
      setShowOAuthMessage(false);
      setSecretToken(null);
      processingOAuthRef.current = false;
      return;
    }

    if (!xTENANTID) {
      setIsLoading(false);
      return;
    }

    const startOAuthFlow = async () => {
      setIsLoading(true);
      try {
        const oauthData = await getOAuthToken();

        if (oauthData.hasToken) {
          await requestSessionGrant();
          return;
        }

        if (!oauthData.redirectUrl) {
          throw new Error("Redirect URL not received from server");
        }

        const popup = window.open(
          oauthData.redirectUrl,
          "FullscriptOAuth",
          "width=800,height=600,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=yes"
        );

        if (popup && !popup.closed) {
          popupWindowRef.current = popup;
          setShowOAuthMessage(true);
          setIsLoading(false);
          startPopupPolling();
          return;
        }

        window.location.href = oauthData.redirectUrl;
      } catch (error) {
        setIsLoading(false);
      }
    };

    void startOAuthFlow();

    return () => {
      clearPolling();
      closePopup();
    };
  }, [isOpen, xTENANTID]);

  useEffect(() => {
    if (!isOpen || !secretToken) return;

    const initializeFullscript = async () => {
      try {
        const Fullscript = await loadFullscript();
        if (!Fullscript) {
          throw new Error("Fullscript library not available");
        }

        const publicKey = import.meta.env.VITE_FULLSCRIPT_PUBLIC_KEY;
        const env = import.meta.env.VITE_FULLSCRIPT_ENV || "us-snd";

        const fullscriptClient = (
          Fullscript as (args: { publicKey: string; env: string }) => {
            create: (
              feature: string,
              options: { secretToken: string; entrypoint: string }
            ) => { mount: (elementId: string) => void; unmount?: () => void };
          }
        )({ publicKey, env });

        const platformFeature = fullscriptClient.create("platform", {
          secretToken,
          entrypoint,
        });

        await new Promise<void>((resolve) => {
          window.requestAnimationFrame(() => resolve());
        });
        await waitForMountPoint(mountContainerIdRef.current);
        featureRef.current = platformFeature;
        await Promise.resolve(
          (
            platformFeature as {
              mount: (target: string) => void | Promise<void>;
            }
          ).mount(mountContainerIdRef.current)
        );
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    };

    void initializeFullscript();

    return () => {
      if (featureRef.current?.unmount) {
        featureRef.current.unmount();
      }
      featureRef.current = null;
    };
  }, [isOpen, secretToken]);

  return (
    <Dialog
      open={isOpen}
      onClose={close}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          width: "95vw",
          maxWidth: "95vw",
          height: "95vh",
          maxHeight: "95vh",
        },
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", pr: 1 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          Order Lab Tests - Fullscript
        </Typography>
        <IconButton onClick={close} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          {showOAuthMessage ? (
            <Box
              sx={{
                minHeight: 300,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 3,
                textAlign: "center",
              }}
            >
              <Box sx={{ maxWidth: 540 }}>
                <InfoOutlinedIcon color="info" sx={{ fontSize: 36, mb: 1 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Complete OAuth Authentication
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  A popup window was opened for Fullscript OAuth. Complete
                  authentication there, then this window will continue
                  automatically.
                </Typography>
                {popupWindowRef.current && !popupWindowRef.current.closed && (
                  <Button
                    variant="contained"
                    onClick={() => popupWindowRef.current?.focus()}
                  >
                    Focus Popup Window
                  </Button>
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{ position: "relative", flex: 1 }}>
              <Box
                id={mountContainerIdRef.current}
                sx={{ width: "100%", height: "100%", minHeight: "800px" }}
              />
              {isLoading && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 2,
                    backgroundColor: "rgba(255, 255, 255, 0.85)",
                  }}
                >
                  <CircularProgress />
                  <Typography color="text.secondary">
                    Loading Fullscript...
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default FullScriptIframe;
